import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import streamChatService from '../services/streamChat';
import { authFetch } from '../utils/authFetch';

const ChatContext = createContext({
  client: null,
  isConnected: false,
  channels: [],
  activeChannel: null,
  unreadCount: 0,
  isLoading: false,
  error: null,
  initializeChat: () => {},
  disconnectChat: () => {},
  setActiveChannel: () => {},
  sendMessage: () => {},
  markAsRead: () => {},
  refreshChannels: () => {}
});

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authToken, setAuthToken] = useState(null);

  const clientRef = useRef(null);
  const initializingRef = useRef(false);

  // Initialize chat connection
  const initializeChat = useCallback(async (authToken) => {
    // If already connected, ensure it's for the same authenticated user.
    // If not, disconnect and re-initialize to avoid leaking prior user's channels.
    try {
      if (authToken) {
        const payload = JSON.parse(atob(authToken.split('.')[1] || ''));
        const persona = payload?.activePersona || payload?.userType;
        const expectedStreamUserId = payload?.userId && persona
          ? `${persona}-${payload.userId}`
          : null;
        const currentStreamUserId = streamChatService.currentUserId;

        if (isConnected && expectedStreamUserId && currentStreamUserId && expectedStreamUserId !== currentStreamUserId) {
          // Logged in user changed – reset client
          streamChatService.disconnect();
          clientRef.current = null;
          setClient(null);
          setIsConnected(false);
          setChannels([]);
          setActiveChannel(null);
          setUnreadCount(0);
        }
      }
    } catch (_) {
      // Ignore decode errors; we'll proceed with normal init
    }

    if (isConnected || initializingRef.current) {
      return;
    }

    initializingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Fetch Stream token from backend using authFetch for 401 handling
      const response = await authFetch('/api/messaging/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      }, { disconnectChat: () => streamChatService.disconnect() });

      if (!response.ok) {
        throw new Error('Failed to get chat token');
      }

      const { token, userId, userData } = await response.json();

      // Store the auth token for later use
      setAuthToken(authToken);

      // Initialize Stream client
      const streamClient = await streamChatService.initialize(userId, token, userData);
      
      if (!streamClient) {
        // Mock mode - Stream not configured
        console.warn('Running in mock mode - Stream not configured');
        setIsConnected(false);
        setIsLoading(false);
        return;
      }

      clientRef.current = streamClient;
      setClient(streamClient);
      setIsConnected(true);

    } catch (err) {
      console.error('Failed to initialize chat:', err);
      setError(err.message);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
      initializingRef.current = false;
    }
  }, [isConnected]);

  // Refresh channel list
  const refreshChannels = useCallback(async () => {
    if (!clientRef.current) return;

    try {
      const streamChannels = await streamChatService.queryChannels();
      const formattedChannels = await Promise.all(
        streamChannels.map(channel => streamChatService.formatChannelForUI(channel))
      );
      
      setChannels(formattedChannels);
      
      // Calculate total unread count
      const totalUnread = formattedChannels.reduce(
        (sum, channel) => sum + channel.unreadCount, 
        0
      );
      setUnreadCount(totalUnread);
    } catch (err) {
      console.error('Failed to refresh channels:', err);
    }
  }, []);

  // Load channels as soon as client is available (after re-render)
  useEffect(() => {
    if (client) {
      refreshChannels();
    }
  }, [client, refreshChannels]);

  // Periodically refresh unread counts
  useEffect(() => {
    if (!clientRef.current) return;
    const interval = setInterval(() => {
      refreshChannels();
    }, 30_000); // every 30s
    return () => clearInterval(interval);
  }, [client, refreshChannels]);

  // Set active channel
  const handleSetActiveChannel = useCallback(async (channelId) => {
    if (!clientRef.current) return;

    try {
      const channel = await streamChatService.getChannel(channelId);
      if (channel) {
        setActiveChannel(channel);
        await streamChatService.markAsRead(channelId);
        
        // Update unread count using the setter function to avoid stale closure
        setChannels(prev => {
          const updated = prev.map(ch => 
            ch.id === channelId ? { ...ch, unreadCount: 0 } : ch
          );
          const newTotal = updated.reduce((sum, ch) => sum + ch.unreadCount, 0);
          setUnreadCount(newTotal);
          return updated;
        });
      }
    } catch (err) {
      console.error('Failed to set active channel:', err);
    }
  }, []);

  // Send message
  const sendMessage = useCallback(async (text, attachments = []) => {
    if (!activeChannel) return;

    try {
      await streamChatService.sendMessage(activeChannel.id, {
        text,
        attachments
      });
    } catch (err) {
      console.error('Failed to send message:', err);
      throw err;
    }
  }, [activeChannel]);

  // Mark channel as read
  const markAsRead = useCallback(async (channelId) => {
    if (!clientRef.current) return;

    try {
      await streamChatService.markAsRead(channelId);
      
      setChannels(prev => {
        const updated = prev.map(ch => 
          ch.id === channelId ? { ...ch, unreadCount: 0 } : ch
        );
        const newTotal = updated.reduce((sum, ch) => sum + ch.unreadCount, 0);
        setUnreadCount(newTotal);
        return updated;
      });
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }, []);

  // Start new conversation
  const startConversation = useCallback(async (targetUserId, targetUserData) => {
    if (!clientRef.current) return null;

    try {
      // Pass the auth token to createDirectChannel for user creation if needed
      const channel = await streamChatService.createDirectChannel(targetUserId, targetUserData, authToken);
      if (channel) {
        // Set active channel immediately so the UI can render the thread
        setActiveChannel(channel);

        // Format just this channel and inject into state so MessagingCenter can resolve it
        // This ensures the thread renders immediately without waiting for a full refresh
        try {
          const formattedChannel = await streamChatService.formatChannelForUI(channel);
          setChannels(prev => {
            // Check if channel already exists in the list
            const exists = prev.some(ch => ch.id === formattedChannel.id);
            if (exists) {
              // Update existing entry
              return prev.map(ch => ch.id === formattedChannel.id ? formattedChannel : ch);
            }
            // Prepend new channel to the top of the list
            return [formattedChannel, ...prev];
          });
        } catch (formatErr) {
          console.warn('Failed to format new channel, injecting minimal entry:', formatErr);
          // Inject a minimal conversation entry so UI can still render the thread
          const minimalChannel = {
            id: channel.id,
            name: targetUserData?.name || targetUserData?.handle || 'New Conversation',
            avatar: targetUserData?.image || '/images/content/avatar-1.jpg',
            handle: targetUserData?.handle || null,
            first_name: targetUserData?.first_name || targetUserData?.name?.split(' ')[0] || null,
            lastMessage: '',
            timestamp: new Date().toISOString(),
            unreadCount: 0,
            isOnline: false,
            isGroup: false,
            memberCount: 2,
            members: [],
            isTyping: false
          };
          setChannels(prev => {
            const exists = prev.some(ch => ch.id === minimalChannel.id);
            if (exists) {
              return prev.map(ch => ch.id === minimalChannel.id ? minimalChannel : ch);
            }
            return [minimalChannel, ...prev];
          });
        }

        // Trigger full refresh in background (not awaited) to reconcile ordering/unreads
        refreshChannels().catch(err => {
          console.warn('Background channel refresh failed:', err);
        });

        return channel;
      }
    } catch (err) {
      console.error('Failed to start conversation:', err);
      throw err;
    }
  }, [refreshChannels, authToken]);

  // Search users
  const searchUsers = useCallback(async (searchTerm, options = {}) => {
    if (!clientRef.current) return [];

    try {
      return await streamChatService.searchUsers(searchTerm, {}, options);
    } catch (err) {
      console.error('Failed to search users:', err);
      return [];
    }
  }, []);

  // Delete channel/conversation
  const deleteChannel = useCallback(async (channelId) => {
    if (!clientRef.current) return;

    try {
      // Pass the auth token to deleteChannel for server-side deletion if needed
      await streamChatService.deleteChannel(channelId, authToken);
      
      // Remove channel from local state and recompute unread
      setChannels(prev => {
        const updated = prev.filter(ch => ch.id !== channelId);
        const newTotal = updated.reduce((sum, ch) => sum + ch.unreadCount, 0);
        setUnreadCount(newTotal);
        return updated;
      });
      
      // Clear active channel if it was deleted
      if (activeChannel?.id === channelId) {
        setActiveChannel(null);
      }
      
      return true;
    } catch (err) {
      console.error('Failed to delete channel:', err);
      throw err;
    }
  }, [activeChannel, authToken]);

  // Disconnect chat
  const disconnectChat = useCallback(() => {
    streamChatService.disconnect();
    clientRef.current = null;
    initializingRef.current = false;
    setClient(null);
    setIsConnected(false);
    setChannels([]);
    setActiveChannel(null);
    setUnreadCount(0);
    setError(null);
    setAuthToken(null);
  }, []);

  // Set up real-time listeners
  useEffect(() => {
    if (!client) return;

    const handleMessageNew = client.on('message.new', (event) => {
      refreshChannels();
      
      if (activeChannel && event.channel_id === activeChannel.id) {
        markAsRead(event.channel_id);
      }
    });

    const handleNotificationNewMessage = client.on('notification.message_new', (event) => {
      if (!activeChannel || event.channel.id !== activeChannel.id) {
        refreshChannels();
      }
    });

    const handleChannelUpdated = client.on(['channel.updated', 'channel.deleted'], () => {
      refreshChannels();
    });

    const handleUserPresenceChanged = client.on('user.presence.changed', () => {
      refreshChannels();
    });

    return () => {
      handleMessageNew.unsubscribe();
      handleNotificationNewMessage.unsubscribe();
      handleChannelUpdated.unsubscribe();
      handleUserPresenceChanged.unsubscribe();
    };
  }, [client, activeChannel, refreshChannels, markAsRead]);

  const value = {
    client,
    isConnected,
    channels,
    activeChannel,
    unreadCount,
    isLoading,
    error,
    initializeChat,
    disconnectChat,
    setActiveChannel: handleSetActiveChannel,
    sendMessage,
    markAsRead,
    refreshChannels,
    startConversation,
    searchUsers,
    deleteChannel
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;
